import { useCallback, useEffect, useState } from "react";
import { LogIn, LogOut, Coffee, Play, MapPin, Download, Check, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/pages/app/AppLayout";
import { api, API_BASE, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CorrectionDialog, CorrectionsPanel } from "@/components/app/Corrections";
import { ClockPanel as NewClockPanel } from "@/components/app/ClockPanel";

const fmt = (s) => [Math.floor(s / 3600), Math.floor((s % 3600) / 60), Math.floor(s % 60)].map((n) => String(n).padStart(2, "0")).join(":");
const t = (v, tz) => (v ? new Date(v).toLocaleTimeString("en-AU", { timeZone: tz, hour: "2-digit", minute: "2-digit" }) : "—");
export const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const monday = (d = new Date()) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const getPos = () => new Promise((res) => {
  if (!navigator.geolocation) { res(null); return; }
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy }), () => res(null), { enableHighAccuracy: true, timeout: 10000 });
});

export const useWorkplaces = () => {
  const [wps, setWps] = useState([]);
  useEffect(() => {
    api.get("/workplaces").then((r) => setWps(r.data)).catch(() => {});
  }, []);
  return wps;
};

const ClockPanel = NewClockPanel;

const LiveTable = () => {
  const tz = useAuth().business?.timezone;
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const load = () => api.get("/attendance/live").then((r) => setRows(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);
  const label = { working: ["Working", "bg-emerald-50 text-emerald-700"], break: ["On break", "bg-amber-50 text-amber-800"], out: ["Clocked out", "bg-slate-100 text-slate-600"] };
  const tag = (g) => g && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: `${g.color}22`, color: g.color }}><span className="h-2 w-2 rounded-full" style={{ background: g.color }} />{g.name}</span>;
  const flag = (r) => (r.missing ? <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700">Not clocked in · {r.late_minutes} min{r.running_late ? ` (said ${r.running_late.minutes} min late)` : ""}</span>
    : r.late_minutes ? <span className="rounded-full bg-[#FF8A00]/15 px-2 py-0.5 text-[11px] font-bold text-[#B35F00]">Late {r.late_minutes} min</span> : null);
  if (!rows.length) return <p className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="live-empty">No active employees yet.</p>;
  return (
    <div data-testid="live-attendance-table">
      <div className="space-y-3 md:hidden">{rows.map((r) => (
        <div key={r.membership_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`live-card-${r.membership_id}`}>
          <div className="flex items-start justify-between gap-2"><p className="min-w-0 truncate font-semibold">{r.name}</p><span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${label[r.state][1]}`}>{label[r.state][0]}</span></div>
          <div className="mt-2 flex flex-wrap gap-1.5">{tag(r.job_role)}{tag(r.department)}{flag(r)}</div>
          <p className="mt-2 text-xs text-slate-500">{r.workplace || "No workplace"} · Rostered {r.rostered ? `${r.rostered.start}–${r.rostered.end}` : "—"}</p>
          {r.shift && <p className="font-mono-num mt-1 text-sm">In {t(r.shift.clock_in, tz)} · {r.shift.worked_hours.toFixed(2)} h{r.shift.exception && <span className="ml-2 text-xs font-semibold text-red-600">Location exception</span>}</p>}
        </div>))}</div>
      <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white md:block">
        <table className="w-full text-sm"><thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Employee", "Status", "Workplace", "Rostered today", "Clock in", "Hours", ...(rows.some((r) => r.hourly_rate != null) ? ["Rate"] : []), "Flags"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.membership_id} className="border-t border-slate-100" data-testid={`live-row-${r.membership_id}`}>
              <td className="px-4 py-3"><p className="font-medium">{r.name}</p><div className="mt-1 flex flex-wrap gap-1">{tag(r.job_role)}{tag(r.department)}</div></td>
              <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${label[r.state][1]}`}>{label[r.state][0]}</span></td>
              <td className="px-4 py-3 text-xs">{r.workplace || "—"}</td>
              <td className="px-4 py-3">{r.rostered ? `${r.rostered.start}–${r.rostered.end}` : "—"}</td>
              <td className="font-mono-num px-4 py-3">{t(r.shift?.clock_in, tz)}</td>
              <td className="font-mono-num px-4 py-3">{r.shift ? r.shift.worked_hours.toFixed(2) : "—"}</td>
              {rows.some((x) => x.hourly_rate != null) && <td className="font-mono-num px-4 py-3">{r.hourly_rate != null ? `$${Number(r.hourly_rate).toFixed(2)}` : "—"}</td>}
              <td className="px-4 py-3 text-xs"><div className="flex flex-wrap gap-1">{flag(r)}{r.shift?.exception && <span className="font-semibold text-red-600">Location exception</span>}</div></td>
            </tr>))}</tbody></table>
      </div>
    </div>
  );
};

export const TimeClockPage = ({ live = false }) => {
  const { membership } = useAuth();
  const mgr = membership.role !== "employee";
  const owner = membership.role === "owner";
  const showClock = !live && !owner;
  return (
    <div data-testid="time-clock-page">
      <PageHeader eyebrow="Time clock" title={live || owner ? "Live attendance" : "Clock in & out"} />
      <div className={`grid gap-6 ${mgr && showClock ? "xl:grid-cols-2" : ""}`}>
        {showClock && <ClockPanel />}
        {mgr && <div><h2 className="mb-3 font-semibold">Who's working now</h2><LiveTable /></div>}
      </div>
    </div>
  );
};

const ReviewDialog = ({ r, action, start, end, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const submit = async () => {
    try { await api.post(`/timesheets/${action}`, { membership_id: r.membership_id, start, end, reason }); toast.success(action === "reject" ? "Timesheet sent back to the employee" : "Timesheet reopened"); onDone(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ts-review-dialog">
        <DialogHeader><DialogTitle>{action === "reject" ? `Reject ${r.name}'s timesheet` : `Reopen ${r.name}'s timesheet`}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{action === "reject" ? "The employee is notified with your reason." : "This unlocks the timesheet so hours can be changed. It's recorded in the audit log."}</p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="ts-review-reason" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button disabled={reason.trim().length < 3} onClick={submit} className="bg-[#0B2D5B]" data-testid="ts-review-submit">{action === "reject" ? "Reject" : "Reopen"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TimesheetsPage = () => {
  const { membership, business } = useAuth();
  const tz = business?.timezone;
  const mgr = membership.role !== "employee";
  const [start, setStart] = useState(isoDay(monday()));
  const [end, setEnd] = useState(isoDay(addDays(monday(), 6)));
  const [rows, setRows] = useState([]);
  const [fix, setFix] = useState(null);
  const [corrKey, setCorrKey] = useState(0);
  const wps = useWorkplaces();
  const load = useCallback(() => {
    let stale = false;
    api.get("/timesheets", { params: { start, end } }).then((r) => { if (!stale) setRows(r.data); }).catch((e) => toast.error(formatError(e)));
    return () => { stale = true; };
  }, [start, end]);
  useEffect(() => load(), [load]);
  const [reviews, setReviews] = useState([]);
  const [review, setReview] = useState(null);
  useEffect(() => {
    api.get("/timesheets/reviews", { params: { start, end } }).then((r) => setReviews(r.data)).catch(() => {});
  }, [start, end, rows]);
  const approve = async (r) => {
    try { await api.post("/timesheets/approve", { membership_id: r.membership_id, start, end }); toast.success("Approved & locked"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const pending = rows.filter((r) => !r.approval);
  const approveAll = async () => {
    const results = await Promise.allSettled(pending.map((r) => api.post("/timesheets/approve", { membership_id: r.membership_id, start, end })));
    const failed = results.filter((x) => x.status === "rejected").length;
    if (failed) toast.error(`${failed} timesheet${failed > 1 ? "s" : ""} couldn't be approved`);
    else toast.success(`Approved & locked ${results.length} timesheet${results.length > 1 ? "s" : ""}`);
    load();
  };
  const exportCsv = async () => {
    const res = await api.get("/timesheets/export.csv", { params: { start, end }, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = `timesheets_${start}_${end}.csv`;
    a.click();
  };
  return (
    <div data-testid="timesheets-page">
      <PageHeader eyebrow="Timesheets" title="Timesheets">
        <div className="flex flex-wrap gap-2">
          {mgr ? <>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40 rounded-xl" data-testid="ts-start" />
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40 rounded-xl" data-testid="ts-end" />
          </> : <span className="self-center rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600" data-testid="ts-this-week-label">This week</span>}
          {mgr && pending.length > 0 && <Button onClick={approveAll} className="rounded-xl bg-[#00C16B] hover:bg-[#00A85C]" data-testid="ts-approve-all-btn"><Check className="mr-1 h-4 w-4" />Approve all ({pending.length})</Button>}
          {mgr && <Button variant="outline" onClick={exportCsv} className="rounded-xl" data-testid="ts-export-btn"><Download className="mr-1 h-4 w-4" />CSV</Button>}
          <Button variant="outline" onClick={() => setFix({})} className="rounded-xl" data-testid="ts-fix-missed-btn"><Wrench className="mr-1 h-4 w-4" />Fix a missed clock</Button>
        </div>
      </PageHeader>
      <CorrectionsPanel mgr={mgr} refreshKey={corrKey} onDecided={load} />
      {review && <ReviewDialog {...review} start={start} end={end} onClose={() => setReview(null)} onDone={() => { setReview(null); load(); }} />}
      {fix && <CorrectionDialog shift={fix} wps={wps} onClose={() => setFix(null)} onSaved={() => { setFix(null); setCorrKey((k) => k + 1); }} />}
      <div className="space-y-4">
        {rows.length === 0 && <p className="rounded-2xl bg-white p-8 text-center text-slate-500" data-testid="ts-empty">No shifts recorded in this period.</p>}
        {rows.map((r) => (
          <div key={r.membership_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`ts-card-${r.membership_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><p className="font-semibold">{r.name}</p><p className="font-mono-num text-sm text-slate-500">{r.worked_hours.toFixed(2)} h worked · {r.break_hours.toFixed(2)} h breaks{r.est_gross != null && ` · est. $${r.est_gross.toFixed(2)} ${r.wage_type && r.wage_type !== "hourly" ? "fixed wage" : r.hourly_rate != null ? `at hourly rate $${Number(r.hourly_rate).toFixed(2)}` : ""}`}</p></div>
              <div className="flex flex-wrap items-center gap-2">
                {r.approval ? <span className="flex items-center gap-1 text-sm font-semibold text-emerald-700" data-testid={`ts-approved-${r.membership_id}`}><Check className="h-4 w-4" />Approved & locked</span>
                  : mgr && <Button onClick={() => approve(r)} className="rounded-full bg-[#00C16B] hover:bg-[#00A85C]" data-testid={`ts-approve-${r.membership_id}`}>Approve</Button>}
                {mgr && !r.approval && <Button variant="outline" onClick={() => setReview({ r, action: "reject" })} className="rounded-full" data-testid={`ts-reject-${r.membership_id}`}>Reject</Button>}
                {mgr && r.approval && <Button variant="outline" onClick={() => setReview({ r, action: "reopen" })} className="rounded-full" data-testid={`ts-reopen-${r.membership_id}`}>Reopen</Button>}
                {mgr && <Link to={`/app/employees/${r.membership_id}?tab=hours`} className="text-xs font-semibold text-[#00A85C]" data-testid={`ts-add-hours-${r.membership_id}`}>Add or edit hours</Link>}
              </div>
            </div>
            {reviews.filter((x) => x.membership_id === r.membership_id).slice(0, 1).map((x) => <p key={x.review_id} className="mt-2 rounded-lg bg-[#FF8A00]/10 px-3 py-2 text-xs text-[#8A4A00]" data-testid={`ts-review-${r.membership_id}`}>{x.action === "rejected" ? "Sent back" : "Reopened"} by {x.by}: {x.reason}</p>)}
            <ul className="mt-3 divide-y divide-slate-100 text-sm">{r.shifts.map((s) => (
              <li key={s.shift_id} className="flex justify-between py-2"><span>{new Date(s.clock_in).toLocaleDateString("en-AU", { timeZone: tz, weekday: "short", day: "numeric", month: "short" })} · {t(s.clock_in, tz)}–{s.clock_out ? t(s.clock_out, tz) : "now"}{s.exception && <span className="ml-2 text-xs font-semibold text-red-600">location exception</span>}</span><span className="flex items-center gap-3">{s.corrected && <span className="text-xs text-slate-400">corrected</span>}<span className="font-mono-num">{s.worked_hours.toFixed(2)} h</span>{!r.approval && s.membership_id === membership.membership_id && <button onClick={() => setFix(s)} className="text-xs font-semibold text-[#00A85C]" data-testid={`ts-fix-${s.shift_id}`}>Fix</button>}</span></li>
            ))}</ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export { API_BASE };
